// DS: STU3.4
import type { FC } from 'react';

import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  DndSortableChildrenProps,
  DndSortableElement,
} from '@wix/da-shared-react/pkg/Dnd/DndSortable';
import { DraggableType } from '@wix/da-shared-react/pkg/types/dnd';
import {
  getActiveSection,
  getStudioParams,
  getIsDndReorderSupported,
} from '../../../../selectors/studio';
import { StudioSection } from '../../../../types/store';
import DeviationThumb from '../../_partials/DeviationThumb';
import DeviationTitle from '../../_partials/DeviationTitle';
import DeviationTags from '../../_partials/DeviationTags';
import DeviationPublishedTime from '../../_partials/DeviationPublishedTime';
import DeviationStats from '../../_partials/DeviationStats';
import DeviationFlags from '../../_partials/DeviationFlags';
import DeviationActions from '../../../tableActions/DeviationActions';
import { StudioDeviationListContext } from '../../context';
import BaseListCell from '../BaseListItem/BaseListCell';
import SelectableListItem from '../SelectableListItem';

import s from './DeviationListItem.scss';

export interface Props {
  deviation: PapiDeviation;
  offset: number;
  selected: number[];
  onSelect: (index: number, isSelected: boolean, isShiftKey?: boolean) => void;
  sortableProps?: DndSortableChildrenProps;
  className?: string;
  cellClassName?: string;
}

const DeviationListItem: FC<Props> = ({
  offset,
  selected,
  onSelect,
  deviation,
  sortableProps = {} as DndSortableChildrenProps,
  className,
  cellClassName,
}) => {
  const activeSection = useSelector(getActiveSection);
  const { contentType } = useSelector(getStudioParams);
  const isDndReorderSupported = useSelector(getIsDndReorderSupported);
  const { draggedElementId, ...dndProps } = sortableProps;
  const { tagsColRef, tagsColWidth, actionsColRef, actionsColWidth } =
    useContext(StudioDeviationListContext);

  const showTagsAndFlags =
    activeSection !== StudioSection.JOURNALS && contentType !== 'posts';
  const isSelected = selected.includes(offset);

  // We use the first row as a probe to determine the size of columns
  const shouldAddColumnRefs = offset === 0;

  const flags = useMemo(
    () => showTagsAndFlags && <DeviationFlags deviation={deviation} />,
    [showTagsAndFlags, deviation]
  );
  const stats = useMemo(
    () => <DeviationStats className={s['stats']} deviation={deviation} />,
    [deviation]
  );

  const renderRow = useCallback(
    sortableOptions => (
      <SelectableListItem
        ref={sortableOptions && sortableOptions.ref}
        className={cn(s[`section-${activeSection.toLowerCase()}`], className)}
        selectCellClassName={s['cell-checkbox']}
        offset={offset}
        selected={selected}
        onSelect={onSelect}
        isDragging={Boolean(
          draggedElementId &&
            (draggedElementId === deviation.deviationId || isSelected)
        )}
      >
        <BaseListCell className={cn(s['cell-thumb'], cellClassName)}>
          <DeviationThumb deviation={deviation} />
        </BaseListCell>
        <BaseListCell className={cn(s['cell-title'], cellClassName)}>
          <DeviationTitle deviation={deviation} />
          <div className={s['timestamp-and-flags']}>
            <DeviationPublishedTime
              className={s['timestamp']}
              deviation={deviation}
            />

            {flags && <div className={s['separator']}></div>}
            {flags}
          </div>
          <div className={s['stats-and-tags']}>
            {showTagsAndFlags && (
              <DeviationTags
                className={s['deviation-tags']}
                deviation={deviation}
                collapsed
              />
            )}
            {stats}
          </div>
        </BaseListCell>
        <BaseListCell
          className={cn(s['cell-stats-and-tags'], cellClassName)}
          ref={shouldAddColumnRefs ? tagsColRef : undefined}
        >
          {stats}
          {showTagsAndFlags && (
            <DeviationTags
              className={s['deviation-tags']}
              deviation={deviation}
              columnWidth={tagsColWidth}
            />
          )}
        </BaseListCell>
        <BaseListCell
          className={cn(s['cell-actions'], cellClassName)}
          ref={shouldAddColumnRefs ? actionsColRef : undefined}
        >
          <DeviationActions
            deviation={deviation}
            offset={offset}
            columnWidth={actionsColWidth}
            className={cn(
              // We have to put `visibility: hidden` on the content instead of the above `<div>` to avoid
              // the row's background color being clipped where this cell beings.
              selected.length > 0 && s['hidden-cell-content']
            )}
          />
        </BaseListCell>
      </SelectableListItem>
    ),
    [
      activeSection,
      offset,
      selected,
      isSelected,
      onSelect,
      deviation,
      className,
      cellClassName,
      draggedElementId,
      flags,
      stats,
      tagsColRef,
      tagsColWidth,
      actionsColRef,
      actionsColWidth,
      shouldAddColumnRefs,
      showTagsAndFlags,
    ]
  );

  if (isDndReorderSupported) {
    return (
      <DndSortableElement
        dndType={DraggableType.STUDIO_TABLE_ROW}
        dndAcceptType={DraggableType.STUDIO_TABLE_ROW}
        id={deviation.deviationId}
        key={deviation.deviationId}
        position={offset + 1}
        customPreview
        {...(dndProps! as Omit<DndSortableChildrenProps, 'draggedElementId'>)}
      >
        {sortableOptions => renderRow(sortableOptions)}
      </DndSortableElement>
    );
  }

  return renderRow({});
};
DeviationListItem.displayName = 'DeviationListItem';

export default DeviationListItem;
